import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "@/components/icons"

import { cn } from "@/lib/utils"
import { containsElementOfType, FALLBACK_DIALOG_TITLE, FALLBACK_DIALOG_DESCRIPTION } from "@/lib/dialog-a11y"
import { useVisualViewportMaxHeight, useScrollFocusIntoView } from "@/lib/viewportFit"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// A dialog is capped to the VISUAL viewport, so an open software keyboard can
// never bury it. See lib/viewportFit.js for why vh and dvh are not enough.
//
// Only the BODY scrolls: the footer is a SIBLING of the scroll region, so the
// submit button is on screen whatever the scroll position is. It used to sit
// inside the scroll region, which made "is the submit reachable" depend on
// scrolling the whole dialog first — and the keyboard-focus helper in
// viewportFit.js smooth-scrolls that same container at the same moment, so a
// long dialog could settle with the button a few pixels below the fold.
const DialogContent = React.forwardRef(({ className, children, srTitle, srDescription, style, ...props }, ref) => {
  const hasTitle = containsElementOfType(children, [DialogPrimitive.Title, DialogTitle]);
  const hasDescription = containsElementOfType(children, [DialogPrimitive.Description, DialogDescription]);
  const kids = React.Children.toArray(children);
  const footer = kids.filter((k) => k.type === DialogFooter);
  const body = kids.filter((k) => k.type !== DialogFooter);
  const fit = useVisualViewportMaxHeight();
  const own = React.useRef(null);
  useScrollFocusIntoView(own);
  const setRefs = (node) => {
    own.current = node;
    if (typeof ref === "function") ref(node); else if (ref) ref.current = node;
  };
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      style={{ ...fit, ...style }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 flex max-h-[85dvh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 overflow-hidden border bg-background p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {!hasTitle && (
        <DialogPrimitive.Title className="sr-only">{srTitle || FALLBACK_DIALOG_TITLE}</DialogPrimitive.Title>
      )}
      {!hasDescription && (
        <DialogPrimitive.Description className="sr-only">
          {srDescription || FALLBACK_DIALOG_DESCRIPTION}
        </DialogPrimitive.Description>
      )}
      <div
        data-dialog-body
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain">
        {body}
      </div>
      {footer}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2 [&>*]:min-h-11 sm:[&>*]:min-h-9", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-kop font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-body text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
